import { cn } from "@/lib/utils";
import React from "react";

type BadgeColor =
  | "gray"
  | "red"
  | "yellow"
  | "green"
  | "blue"
  | "indigo"
  | "purple"
  | "pink"
  | "white"
  | "black";

const badgeColors: Record<
  BadgeColor,
  { bg: string; text: string; dot: string }
> = {
  gray: { bg: "bg-gray-100", text: "text-gray-600", dot: "fill-gray-400" },
  white: { bg: "bg-white", text: "text-gray-500", dot: "fill-gray-400" },
  red: { bg: "bg-red-100", text: "text-red-700", dot: "fill-red-500" },
  yellow: {
    bg: "bg-yellow-100",
    text: "text-yellow-800",
    dot: "fill-yellow-500",
  },
  green: { bg: "bg-green-100", text: "text-green-700", dot: "fill-green-500" },
  blue: { bg: "bg-blue-100", text: "text-blue-700", dot: "fill-blue-500" },
  indigo: {
    bg: "bg-brand-100",
    text: "text-brand-700",
    dot: "fill-brand-500",
  },
  purple: {
    bg: "bg-purple-100",
    text: "text-purple-700",
    dot: "fill-purple-500",
  },
  pink: { bg: "bg-pink-100", text: "text-pink-700", dot: "fill-pink-500" },
  black: { bg: "bg-black", text: "text-white", dot: "fill-white" },
};

interface BadgeProps {
  color?: BadgeColor;
  dot?: boolean;
  children: React.ReactNode;
  className?: string;
  size?: "md" | "sm";
  onClick?: () => void;
}

export function Badge({
  color = "gray",
  dot = false,
  children,
  className,
  size = "md",
}: BadgeProps) {
  const colors = badgeColors[color];

  return (
    <span
      className={cn(
        "inline-flex items-center gap-x-1.5 rounded-xl px-3 py-0.5 text-xs font-medium",
        size === "md" ? "h-6" : "h-5",
        colors.bg,
        colors.text,
        className,
      )}
    >
      {dot && (
        <svg
          viewBox="0 0 6 6"
          aria-hidden="true"
          className={cn("size-1.5", colors.dot)}
        >
          <circle r={3} cx={3} cy={3} />
        </svg>
      )}
      {children}
    </span>
  );
}
